import { useContext, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import type { MultiPortfolioParseResult, Portfolio } from 'venn-api';
import type { MatchParams } from 'venn-frontend/src/analysis-page/src/utils';
import {
  allocatorAnalysisSubject,
  proxySetIdState,
  hasUnsavedPortfolioChangesInAllocator,
  openAllocatorSubject,
  sendSignal,
} from 'venn-state';
import { AnalysisSubject, Routes, useHasFF } from 'venn-utils';
import { AnalysisContext } from '../../../../contexts';
import UniversalUploaderContext from '../../../../contexts/universal-uploader';
import {
  IAssignOwnershipView,
  IPortfolioUploadConfirmationView,
  IReviewHistoricalPortfolioView,
  IReviewProFormaPortfolioView,
  IUploadPortfolioView,
} from '../../constants';
import type { MultiPortfolioUploadConfirmationData } from '../../types';
import { isHistoricalData } from '../../utils';

export const useMultiPortfolioUploaderState = () => {
  const hasHistoricalPortfolioFeatureFlag = useHasFF('historical_portfolios_ff');
  const hasNewPermissions = useHasFF('new_permissions_ff');
  const proxySetId = useRecoilValue(proxySetIdState);

  const { setView } = useContext(UniversalUploaderContext);
  const [parsedData, setParsedData] = useState<MultiPortfolioParseResult>();
  const [uploadConfirmationData, setUploadConfirmationData] = useState<MultiPortfolioUploadConfirmationData>();

  const analysisRouteMatch = useRouteMatch<MatchParams>(Routes.ANALYSIS_RESULTS_PATH);
  const { forceRefreshAnalysis } = useContext(AnalysisContext);

  const allocatorSubject = useRecoilValue(openAllocatorSubject);
  const maybeUpdatePortfolioInAllocatorPanel = useRecoilCallback(
    ({ snapshot, set }) =>
      async (allocatorSubjectPortfolio: Portfolio) => {
        const hasUnsavedChanges = await snapshot.getPromise(hasUnsavedPortfolioChangesInAllocator);
        if (hasUnsavedChanges) {
          // If portfolio has unsaved changes we don't want to override it
          return;
        }
        const analysisSubject = new AnalysisSubject(allocatorSubjectPortfolio, 'portfolio', proxySetId);
        set(allocatorAnalysisSubject(allocatorSubject), analysisSubject);
      },
    [allocatorSubject, proxySetId],
  );

  const goToReviewStep = (parsedData: MultiPortfolioParseResult) => {
    setParsedData(parsedData);
    if (hasHistoricalPortfolioFeatureFlag && isHistoricalData(parsedData)) {
      setView(IReviewHistoricalPortfolioView);
    } else {
      setView(IReviewProFormaPortfolioView);
    }
  };

  const goBackToUploadStep = () => {
    setParsedData(undefined);
    setView(IUploadPortfolioView);
  };

  const goToAssignOwnership = () => {
    setView(IAssignOwnershipView);
  };

  const goToUploadConfirmation = async (data: MultiPortfolioUploadConfirmationData) => {
    setUploadConfirmationData(data);
    setView(IPortfolioUploadConfirmationView);

    const analysisObjectId = analysisRouteMatch?.params?.objectId;
    const analysisPortfolio = analysisObjectId
      ? data.persistResult.portfolioPersistResults.find((result) => result.portfolio.id.toString() === analysisObjectId)
          ?.portfolio
      : undefined;

    // If we are on the analysis page and user updated portfolio that's currently being analyzed
    // we will force-refresh analysis so that newly uploaded data is reflected in the analysis.
    // This will trigger refresh of the allocator panel and actual analysis results
    // and works for both pro forma and historical portfolios.
    if (analysisRouteMatch && analysisPortfolio) {
      forceRefreshAnalysis();
    }

    // If user uploaded a portfolio that is currently being viewed in the Allocator panel
    // and it does not have unsaved changes we will update allocator panel subject with newly updated version.
    const allocatorSubjectPortfolio = allocatorSubject?.portfolioId
      ? data.persistResult.portfolioPersistResults.find(
          (result) => result.portfolio.id === allocatorSubject.portfolioId,
        )?.portfolio
      : undefined;
    if (allocatorSubjectPortfolio) {
      await maybeUpdatePortfolioInAllocatorPanel(allocatorSubjectPortfolio);
    }
    // Send signal to force-refresh Studio/RL Recoil selectors.
    // We will always send this signal (without checking if we are on Studio/RL page) because
    // Recoil state is global and we want to invalidate any previously cached data
    sendSignal({ type: 'WorkspaceConfigurationUpdate' });
  };

  return {
    parsedData,
    uploadConfirmationData,
    hasNewPermissions,
    goToReviewStep,
    goBackToUploadStep,
    goToAssignOwnership,
    goToUploadConfirmation,
  };
};
